import React, { useEffect, useState, useCallback } from "react";
import { View, StyleSheet, Linking, Platform } from "react-native";
import CustomText from "../components/common/general/CustomText/CustomText";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useRoute, useFocusEffect, RouteProp } from "@react-navigation/native";
import { MotiView } from "moti";
import { searchWeb } from "../services";
import FileCard from "../components/lms/FileCard/FileCard";
import useResponsiveScreen from "../hooks/useResponsiveScreen";
import CustomHtml from "../components/common/general/CustomHtml/CustomHtml";
import SourcesList from "../components/lms/SourcesList/SourcesList";
import ImageCardList from "../components/lms/ImageCardList/ImageCardList";
import VideoCardList from "../components/lms/VideoCardList/VideoCardList";
import Divider from "../components/common/layout/Divider/Divider";
import Loader from "../components/common/status/Loader/Loader";
import { useAppConfig } from "../AppConfigProvider";
import Sentry from "../utils/sentry";

import { useAppState } from "../contexts/AppStateContext";
import ActivityInstructionsModal from "../components/lms/ActivityInstructionsModal/ActivityInstructionsModal";
import { RootStackParamList } from "../navigation/AppNavigator.web";
import PDFViewer from "../components/lms/PDFViewer/PDFViewer";
import YoutubeVideoPlayer from "../components/youtube/YoutubeVideoPlayer";
import DefaultLayout from "../layouts/DefaultLayout.web";
import { ResourceType, useActiveCourse } from "../contexts/ActiveCourseContext";

function CustomResourceStudentView() {
  const [isLoadingSidebarData, setIsLoadingSidebarData] = useState(true);
  const [showVideo, setShowVideo] = useState(true);

  const route = useRoute<RouteProp<RootStackParamList, "Custom Resource Student">>();

  const {
    resource_type: resourceType,
    resource_id: resourceId,
    course_id: courseId,
    integration_id: integrationId,
  } = route.params;

  const { isMedium } = useResponsiveScreen();
  const [isModalVisible, setIsModalVisible] = useState(false);

  const appConfig = useAppConfig();
  const isSuggestedInsights = appConfig.isSuggestedInsights;
  const currentDomain = appConfig?.currentDomain || "";

  const [videoCardData, setVideoCardData] = useState([]);
  const [sources, setSources] = useState([]);
  const [imageCardData, setImageCardData] = useState([]);

  const { dispatch } = useAppState();

  const { axioResourceData, setActiveResource } = useActiveCourse();

  useEffect(() => {
    setActiveResource({ resourceType: resourceType as ResourceType, resourceId });
  }, [resourceType, resourceId]);

  const [selectedFile, setSelectedFile] = useState(null);

  useFocusEffect(
    useCallback(() => {
      // Reset media state when the component is focused
      dispatch({
        type: "SET_MEDIA",
        payload: {
          video: null,
          wiki: null,
        },
      });
      setShowVideo(true);

      return () => {
        setSelectedFile(null);
        setShowVideo(false);
        dispatch({
          type: "SET_MEDIA",
          payload: {
            video: null,
            wiki: null,
          },
        });
      };
    }, [dispatch])
  );

  useEffect(() => {
    if (resourceType === "axioResource" && axioResourceData?.getAxioResource?.data?.[0].name) {
      const id = axioResourceData?.getAxioResource?.data?.[0].id;
      const fetchData = async () => {
        try {
          setIsLoadingSidebarData(true);
          const token = await AsyncStorage.getItem("token");
          const data = await searchWeb(id, token);

          const videos = data.video.map((item) => ({
            imageUrl: item.metadata.videoThumbnail,
            title: item.metadata.title,
            url: item.metadata.source,
          }));

          const images = data.images.flatMap((item) =>
            item.metadata.images.slice(0, 1).map((image) => ({
              imageUrl: image,
              title: item.metadata.title,
              source: item.metadata.source,
            }))
          );

          const wikipedia = data.Wikipedia.map((item) => ({
            content: item.pageContent,
            title: item.metadata.title,
            source: item.metadata.source,
          }));

          setVideoCardData(videos);
          setImageCardData(images);
          setSources(wikipedia);
          setIsLoadingSidebarData(false);
        } catch (error) {
          Sentry.captureException(error);
          setIsLoadingSidebarData(false);
        }
      };

      fetchData();
    }
  }, [axioResourceData, resourceType]);

  const renderFiles = (files) => {
    if (!files || files.length === 0) return null;

    const handleFileClick = async (file) => {
      try {
        handleOpenURL(file.fileURL);
      } catch (error) {
        console.error("Error handling file click:", error);
        handleOpenURL(file.fileURL);
      }
    };

    return (
      <>
        <CustomText text="Files" size="m" weight="bold" />
        <CustomSpacing type="vertical" size="s" />
        <View style={styles.filesContainer}>
          {files.map((file) => (
            <FileCard
              key={file.fileId || file.id}
              title={file.fileName}
              url={file.fileURL}
              onPress={() => handleFileClick(file)}
            />
          ))}
        </View>
        {selectedFile && Platform.OS === "web" && (
          <>
            <CustomSpacing type="vertical" size="l" />
            <View style={styles.previewContainer}>
              <PDFViewer url={selectedFile.fileURL} />
            </View>
          </>
        )}
        <CustomSpacing type="vertical" size="xl" />
      </>
    );
  };

  const renderVideo = () => {
    const videoUrl = axioResourceData?.getAxioResource?.data?.[0].videoUrl;
    const youtubeVideoId = extractYoutubeVideoId(videoUrl);
    if (youtubeVideoId && showVideo) {
      return <YoutubeVideoPlayer id={youtubeVideoId} />;
    }

    return (
      <CustomText
        text={videoUrl}
        useTranslationText={false}
        style={styles.linkText}
        onPress={() => handleOpenURL(videoUrl)}
      />
    );
  };

  const renderSidebar = () => {
    const hasData = sources.length > 0 || imageCardData.length > 0 || videoCardData.length > 0;

    if (!hasData) {
      return null;
    }

    return (
      <MotiView
        from={{ opacity: 0, translateY: 20, scale: 1 }}
        animate={{ opacity: 1, translateY: 0, scale: 1 }}
        delay={10}
        style={styles.sidebarContainer}
        // @ts-expect-error - This is a workaround to fix the type error
        transition={{ type: "timing", duration: 600 }}>
        {sources.length > 0 && (
          <>
            <CustomText text="Sources" style={styles.headerText} size="m" weight="bold" role="heading" aria-level="2" />
            <SourcesList sources={sources} />
            <CustomSpacing type="vertical" size="m" />
          </>
        )}

        {imageCardData.length > 0 && (
          <>
            <CustomText text="Images" style={styles.headerText} size="m" weight="bold" role="heading" aria-level="2" />
            <ImageCardList imageCardData={imageCardData} />
            <CustomSpacing type="vertical" size="m" />
          </>
        )}

        {videoCardData.length > 0 && (
          <>
            <CustomText text="Videos" style={styles.headerText} size="m" weight="bold" role="heading" aria-level="2" />
            <VideoCardList videoCardData={videoCardData} />
            <CustomSpacing type="vertical" size="m" />
          </>
        )}
      </MotiView>
    );
  };

  const handleOpenURL = async (url) => {
    const fullUrl = url.startsWith("/") ? `https://${currentDomain}${url}` : url;

    if (Platform.OS === "web") {
      try {
        const token = await AsyncStorage.getItem("token");

        // Create a form that posts to the URL
        const form = document.createElement("form");
        form.method = "POST";
        form.action = fullUrl;
        form.target = "_blank";

        // Add token as hidden field
        const tokenInput = document.createElement("input");
        tokenInput.type = "hidden";
        tokenInput.name = "token";
        tokenInput.value = token;
        form.appendChild(tokenInput);

        // Submit form
        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
      } catch (err) {
        console.error("Failed to open URL:", err);
        Sentry.captureException(err);
      }
    } else {
      // For mobile platforms
      try {
        const token = await AsyncStorage.getItem("token");

        // Make POST request
        const response = await fetch(fullUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: `token=${encodeURIComponent(token)}`,
        });

        // Get the final URL after redirects
        const finalUrl = response.url;

        // Open the final URL in the device's browser
        await Linking.openURL(finalUrl);
      } catch (err) {
        console.error("Failed to open URL:", err);
        Sentry.captureException(err);
      }
    }
  };

  const extractYoutubeVideoId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^[a-zA-Z0-9_-]{11}$/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  };

  return (
    <DefaultLayout showCourseNav={true} pageHasChat={false}>
      <ActivityInstructionsModal visible={isModalVisible} onClose={() => setIsModalVisible(false)} />

      <View style={isMedium ? styles.mainContainer : styles.mainContainerMobile}>
        <View style={isMedium ? styles.rowContainer : styles.columnContainer}>
          <View style={isMedium ? styles.contentContainer : styles.fullWidthContentContainer}>
            <MotiView
              from={{
                opacity: 0,
                translateY: 20,
                scale: 1,
              }}
              animate={{
                opacity: 1,
                translateY: 0,
                scale: 1,
              }}
              delay={600}
              // @ts-expect-error - This is a workaround to fix the type error
              transition={{ type: "timing", duration: 600 }}
              style={{ paddingHorizontal: 30, paddingVertical: 10 }}>
              <CustomText text="Description" size="m" weight="bold" />
              <CustomSpacing type="vertical" size="s" />
              {resourceType === "axioResource" && axioResourceData?.getAxioResource?.data?.[0].description && (
                <CustomHtml html={axioResourceData?.getAxioResource?.data?.[0].description} />
              )}

              <CustomSpacing type="vertical" size="xl" />

              {/* Files Section */}
              {renderFiles(axioResourceData?.getAxioResource?.data?.[0].files)}

              {/* Video URL Section */}
              {resourceType === "axioResource" && axioResourceData?.getAxioResource?.data?.[0].videoUrl && (
                <>
                  <CustomText text="Video" size="m" weight="bold" />
                  <CustomSpacing type="vertical" size="s" />
                  {renderVideo()}
                  <CustomSpacing type="vertical" size="xl" />
                </>
              )}
            </MotiView>

            <CustomSpacing type="vertical" size="l" />
          </View>

          {/* Sidebar Section */}
          {isSuggestedInsights && (
            <>
              {isMedium ? (
                <View style={[{ width: 300 }, isLoadingSidebarData ? { alignItems: "center" } : {}]}>
                  <CustomText
                    style={{ margin: 12 }}
                    textType="display"
                    text="Suggested Insights"
                    size="xs"
                    bold={false}
                    role="heading"
                    aria-level="1"
                  />
                  {isLoadingSidebarData ? (
                    <View style={styles.insightsCenterView}>
                      <Loader />
                    </View>
                  ) : (
                    <>{renderSidebar()}</>
                  )}
                </View>
              ) : (
                <>
                  <Divider size="l" />
                  <CustomText
                    textType="display"
                    text="Suggested Insights"
                    size="xs"
                    bold={true}
                    weight="bold"
                    role="heading"
                    aria-level="1"
                  />
                  <View style={isLoadingSidebarData ? { alignItems: "center" } : {}}>
                    {isLoadingSidebarData ? (
                      <View style={styles.fullWidthContentContainer}>
                        <Loader />
                      </View>
                    ) : (
                      <>{renderSidebar()}</>
                    )}
                  </View>
                </>
              )}
            </>
          )}
        </View>
      </View>
    </DefaultLayout>
  );
}

const styles = StyleSheet.create({
  mainContainerMobile: {
    height: 20,
  },
  mainContainer: {
    // flex: 1,
    height: 20,
  },
  rowContainer: {
    flexDirection: "row",
    flex: 1,
  },
  columnContainer: {
    flexDirection: "column",
    flex: 1,
  },
  fullWidthContentContainer: {
    padding: 16,
    // height: 20,
  },
  contentContainer: {
    flex: 3, // Takes 3/4 of the width
    padding: 16,
  },
  headerText: {
    marginBottom: 6,
  },
  sidebarContainer: {
    flex: 1, // Takes 1/4 of the width
    justifyContent: "flex-start",
    paddingBottom: 16,
    paddingLeft: 16,
    paddingRight: 16,
  },
  insightsCenterView: {
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  filesContainer: {
    flexDirection: "column",
    gap: 10,
    maxWidth: 320,
  },
  previewContainer: {
    minHeight: 600,
    borderWidth: 1,
    borderColor: "#D9D9D9",
    borderRadius: 10,
    overflow: "hidden",
    backgroundColor: "#fff",
  },
  linkText: {
    color: "blue",
    textDecorationLine: "underline",
    textAlign: "left",
    borderWidth: 0,
  },
});

export default CustomResourceStudentView;
